import { applyEdges, applyMoney, applyPhone } from './captureMode';

export const formatDateFromTimestamp = (timestamp, fullDate = true) => {
    const date = new Date(timestamp * 1000);
    if (fullDate) {
        return date.toLocaleString('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    }

    return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

export const formatDateFromISODate = (isoDate, fullDate = true) => {
    const timestamp = (new Date(isoDate)).getTime() / 1000;
    const date = new Date(timestamp * 1000);
    if (fullDate) {
        return date.toLocaleString('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    }

    return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

const WALL_CLOCK_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;
export const DISPLAY_TIMEZONE = 'America/Sao_Paulo';
export const DISPLAY_UTC_OFFSET = '-03:00';

function partsFromMatch(match) {
    return {
        year: match[1],
        month: match[2],
        day: match[3],
        hour: match[4],
        minute: match[5],
        second: match[6] || '00',
    };
}

function partsFromUtcInstant(instant) {
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: DISPLAY_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const parts = Object.fromEntries(formatter.formatToParts(instant).map((p) => [p.type, p.value]));
    return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second || '00',
    };
}

/** Converte qualquer datetime da API para instante UTC (Date). */
export function parseApiInstant(value) {
    if (value == null || value === '') {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const raw = String(value).trim();
    if (!raw) {
        return null;
    }

    if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
        const instant = new Date(raw);
        return Number.isNaN(instant.getTime()) ? null : instant;
    }

    const naiveMatch = raw.match(WALL_CLOCK_DATETIME_RE);
    if (naiveMatch) {
        const instant = new Date(
            `${naiveMatch[1]}-${naiveMatch[2]}-${naiveMatch[3]}T${naiveMatch[4]}:${naiveMatch[5]}:${naiveMatch[6] || '00'}Z`,
        );
        return Number.isNaN(instant.getTime()) ? null : instant;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Extrai componentes de data/hora em America/Sao_Paulo (UTC-3) para exibição. */
export function parseApiDateTimeParts(value) {
    const instant = parseApiInstant(value);
    if (!instant) {
        return null;
    }
    return partsFromUtcInstant(instant);
}

/** datetime-local (Brasília) → RFC3339 UTC para envio à API. */
export function localInputToApiDateTime(inputValue) {
    if (!inputValue) {
        return null;
    }

    const match = String(inputValue).trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
        return null;
    }

    const sec = match[6] || '00';
    const localWithOffset = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${sec}${DISPLAY_UTC_OFFSET}`;
    const instant = new Date(localWithOffset);
    if (Number.isNaN(instant.getTime())) {
        return null;
    }
    return instant.toISOString();
}

/** RFC3339 UTC da API → valor para datetime-local (Brasília). */
export function apiDateTimeToLocalInput(value) {
    const parts = parseApiDateTimeParts(value);
    if (!parts) {
        return null;
    }
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** @deprecated Prefer parseApiInstant + partsFromUtcInstant. */
export function apiDateToStorageString(value) {
    const instant = parseApiInstant(value);
    if (!instant) {
        return value == null ? '' : String(value);
    }
    return instant.toISOString();
}

/** API/DB datetime without timezone — stored as UTC wall clock. */
export function isWallClockDateTime(value) {
    const raw = String(value || '').trim();
    if (!WALL_CLOCK_DATETIME_RE.test(raw)) {
        return false;
    }
    return !/[zZ]$/.test(raw) && !/[+-]\d{2}:?\d{2}$/.test(raw);
}

export function wallClockToInputDateTime(value) {
    return apiDateTimeToLocalInput(value);
}

export function formatWallClockDateTime(value, fullDate = true) {
    const parts = parseApiDateTimeParts(value);
    if (!parts) {
        return null;
    }
    if (fullDate) {
        return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
    }
    return `${parts.day}/${parts.month}/${parts.year}`;
}

export const formatDateFromISODateUTC3 = (isoDate, fullDate = true) => {
    if (!isoDate) return 'N/A';

    const formatted = formatWallClockDateTime(isoDate, fullDate);
    if (formatted) {
        return formatted;
    }

    return 'Data inválida';
};

// Função unificada para formatar datas (substitui formatDateFromTimestamp)
export const formatDate = (dateValue, fullDate = true) => {
    if (!dateValue) return 'N/A';

    // Se for um timestamp (número), converter para ISO string
    if (typeof dateValue === 'number') {
        const date = new Date(dateValue * 1000);
        return formatDateFromISODateUTC3(date.toISOString(), fullDate);
    }

    // Se for uma string ISO ou Date, usar diretamente
    return formatDateFromISODateUTC3(dateValue, fullDate);
};

export const formatUuid = (uuid) => {
    if (!uuid) return '';
    return uuid.split('-')[0];
};

/** Short id for on-screen display. Masks when capture mode is on. */
export function presentUuid(uuid) {
    return applyEdges(formatUuid(uuid));
}

export const formatCurrencyInCents = (value) => {
    const formatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value / 100);
    return applyMoney(formatted);
};

/**
 * Converte valor de campo monetário para inteiros em centavos.
 * - number: já em centavos (ex.: resposta do GET)
 * - string com vírgula: reais pt-BR (ex.: "18,12", "1.234,56")
 * - string com ponto decimal: reais do NumericFormat/toFixed (ex.: "15.00", "35.90")
 * - string só dígitos: valor bruto em centavos (ex.: "1812" → R$ 18,12)
 */
export function parseMoneyToCents(raw) {
    if (raw === '' || raw == null) {
        return 0;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return Math.max(0, Math.round(raw));
    }

    const str = String(raw).trim();
    if (!str) {
        return 0;
    }

    // pt-BR: vírgula como decimal ("18,12" / "1.234,56")
    if (str.includes(',')) {
        const clean = str.replace(/[^\d,]/g, '').replace(',', '.');
        if (clean === '' || clean === '.') {
            return 0;
        }
        const n = parseFloat(clean);
        return Number.isFinite(n) ? Math.max(0, Math.round(n * 100)) : 0;
    }

    // NumericFormat / toFixed: ponto como decimal ("15.00", "35.9")
    if (str.includes('.')) {
        const clean = str.replace(/[^\d.]/g, '');
        const parts = clean.split('.').filter((part) => part !== '');
        if (parts.length === 0) {
            return 0;
        }
        let n;
        if (parts.length === 1) {
            n = parseFloat(parts[0]);
        } else {
            const decimal = parts[parts.length - 1];
            const integer = parts.slice(0, -1).join('');
            n = parseFloat(`${integer}.${decimal}`);
        }
        return Number.isFinite(n) ? Math.max(0, Math.round(n * 100)) : 0;
    }

    const digits = str.replace(/\D/g, '');
    if (!digits) {
        return 0;
    }
    return Math.max(0, parseInt(digits, 10));
}

/**
 * Para {@link NumericFormat}: `floatValue` está em reais; string de fallback segue a mesma regra que parseMoneyToCents.
 */
export function parseFormattedMoneyToCents(floatValue, stringFallback) {
    if (floatValue != null && Number.isFinite(floatValue)) {
        return Math.max(0, Math.round(floatValue * 100));
    }
    return parseMoneyToCents(stringFallback);
}

export const formatCurrencyInFloat = (value) => {
    const formatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
    return applyMoney(formatted);
};

export const formatPhone = (phone) => {
    if (!phone) return "";

    let d = phone.replace(/\D/g, "");
    // E.164 Brasil (+55): exibe como (DDD) número
    if (d.length === 13 && d.startsWith("55")) {
        d = d.slice(2);
    }
    if (d.length === 12 && d.startsWith("55")) {
        d = d.slice(2);
    }

    if (d.length === 11) {
        return d.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }
    if (d.length === 10) {
        return d.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    }

    return d;
};

/** Phone for on-screen display. Masks when capture mode is on. */
export function presentPhone(phone) {
    return applyPhone(formatPhone(phone));
}

export const formatDocument = (document) => {
    if (!document) return "";

    document = document.replace(/\D/g, ""); // Remove tudo que não for número

    if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"); // CPF
    } else if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5"); // CNPJ
    }

    return document; // Retorna como está se não for CPF nem CNPJ
};

// Função para formatar CEP (exemplo: 81670-130)
export const formatZipCode = (zipCode) => {
    if (!zipCode) return "";

    zipCode = zipCode.replace(/\D/g, ""); // Remove tudo que não for número

    if (zipCode.length === 8) {
        return zipCode.replace(/(\d{5})(\d{3})/, "$1-$2");
    }

    return zipCode; // Retorna como está se não for 8 dígitos
};

/**
 * Monta URL absoluta para ficheiros em `public/` do backend (ex. /images/...).
 * No dev, o React corre noutra porta: <img src="/images/..."> pede ao servidor errado.
 * Usa a mesma origem que `REACT_APP_API_BASE_URL` (ex. .../api/ → origem do PHP).
 */
export function resolvePublicMediaUrl(url) {
    if (url == null || url === '') {
        return '';
    }
    const s = String(url).trim();
    if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) {
        return s;
    }
    const base = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (!base) {
        return s.startsWith('/') ? s : `/${s}`;
    }
    try {
        const normalized = base.includes('://') && !base.endsWith('/') ? `${base}/` : base.endsWith('/') ? base : `${base}/`;
        const u = new URL(normalized);
        const path = s.startsWith('/') ? s : `/${s}`;
        return `${u.origin}${path}`;
    } catch {
        return s.startsWith('/') ? s : `/${s}`;
    }
}
